import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import {
  CreateNpsFamilySurvey,
  UpdateNpsFamilySurvey,
  FiltersNpsFamilySurveys,
  CreateNpsFamilySurveySetup
} from '@/Network/Types/Requests/NpsRequests'
import {
  NpsFamilySurveyResponse,
  ListNpsFamilySurveysResponse,
  NpsFamilySurveySetupResponse
} from '@/Network/Types/Responses/NpsResponses'
import { NpsPublicFormResponse } from '@/Network/Types/Responses/NpsPublicFormResponses'
import { ServiceError } from '@/utils/apiError'

export default class NpsFamilySurveyService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/nps/family-surveys'
  }

  async create(payload: CreateNpsFamilySurvey): Promise<NpsFamilySurveyResponse> {
    try {
      const response = await request.post<NpsFamilySurveyResponse>(this.getCompletePath(), payload)
      return response.data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível vincular a enquete à família.')
    }
  }

  async update(surveyId: number, payload: UpdateNpsFamilySurvey): Promise<NpsFamilySurveyResponse> {
    try {
      const response = await request.put<NpsFamilySurveyResponse>(
        `${this.getCompletePath()}/${surveyId}`,
        payload
      )
      return response.data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar a enquete da família.')
    }
  }

  async getById(surveyId: number): Promise<NpsFamilySurveyResponse> {
    try {
      const response = await request.get<NpsFamilySurveyResponse>(
        `${this.getCompletePath()}/${surveyId}`
      )
      return response.data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar a enquete da família.')
    }
  }

  async delete(surveyId: number): Promise<void> {
    try {
      await request.delete(`${this.getCompletePath()}/${surveyId}`)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível remover a enquete da família.')
    }
  }

  async setup(payload: CreateNpsFamilySurveySetup): Promise<NpsFamilySurveySetupResponse> {
    try {
      const response = await request.post<NpsFamilySurveySetupResponse>(
        `${this.getCompletePath()}/setup`,
        payload
      )
      return response.data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível criar a família.')
    }
  }

  async getActiveSurveys(healthUnitId: number, surveyId?: number): Promise<NpsPublicFormResponse> {
    const params = surveyId ? { surveyId } : undefined
    const data = await request
      .get<NpsPublicFormResponse | NpsPublicFormResponse[]>(
        `${this.getCompletePath()}/active/health-unit/${healthUnitId}`,
        params
      )
      .then((response) => response.data)
      .catch((e: unknown) => {
        throw this.handleError(e, 'Não foi possível carregar a enquete vigente da unidade.')
      })
    // Validação da resposta fora do catch: não pode virar o fallback de erro de API.
    if (Array.isArray(data)) {
      if (data.length === 0)
        throw new ServiceError('Nenhum formulário vigente encontrado para esta unidade.', data)
      return data[0]
    }
    return data
  }

  async list(filters?: FiltersNpsFamilySurveys): Promise<ListNpsFamilySurveysResponse> {
    try {
      const { data } = await request.get<ListNpsFamilySurveysResponse>(
        this.getCompletePath(),
        filters
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as enquetes das famílias.')
    }
  }
}
